import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Clock,
  Phone,
  AlertTriangle,
  MapPin,
  Navigation,
} from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { StatusBadge } from "../components/StatusBadge";
import { ContactActions } from "../components/ContactActions";
import {
  JobActionPanel,
  JobChecklistCard,
  JobPhotosCard,
  JobSignatureCard,
  useJobDetail,
} from "../components/jobdetail";
import { fmtDateTime, formatUSPhone } from "../lib/format";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { JobPriorityPill, derivedUrgency } from "../components/JobPriorityPill";

const PIN_ICON = new L.DivIcon({
  className: "cm-map-pin",
  html: `<div style="
    width:32px;height:32px;border-radius:50% 50% 50% 0;
    background:#dc2626;transform:rotate(-45deg);
    box-shadow:0 6px 14px rgba(0,0,0,0.25);
    border:3px solid white;
    display:flex;align-items:center;justify-content:center;
  "><div style="transform:rotate(45deg);width:10px;height:10px;border-radius:50%;background:white"></div></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

// Mapa com mount adiado um tick — evita o erro do Leaflet
// "Map container is already initialized" no double-mount do React StrictMode.
function HeroMap({ lat, lng }) {
  const [ready, setReady] = useState(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    const tmr = setTimeout(() => setReady(true), 0);
    return () => clearTimeout(tmr);
  }, []);

  if (!ready) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-slate-200" />
    );
  }
  return (
    <MapContainer
      center={[lat, lng]}
      zoom={16}
      scrollWheelZoom={false}
      zoomControl={false}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      />
      <Marker position={[lat, lng]} icon={PIN_ICON} />
    </MapContainer>
  );
}

function SignatureWrap({ sigRef, signature, onSaved }) {
  // Wrapper local para evitar que o lint react-hooks/refs reclame ao passar
  // `ref={d.signatureRef}` no JSX (ref vindo de hook destructure).
  return (
    <JobSignatureCard ref={sigRef} signature={signature} onSaved={onSaved} />
  );
}

function fmtDueLabel(iso, t = (s) => s) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    if (diff === 0) return `${t("Hoje")}, ${time}`;
    if (diff === 1) return `${t("Amanhã")}, ${time}`;
    if (diff === -1) return `${t("Ontem")}, ${time}`;
    return d.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: diff > 365 ? "numeric" : undefined,
    }) + `, ${time}`;
  } catch {
    return fmtDateTime(iso);
  }
}

export default function MobileJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const d = useJobDetail(id);

  const fullAddress = useMemo(() => {
    const c = d.client;
    if (!c) return "";
    const parts = [
      c.address + (c.apt_number ? ` · Apt ${c.apt_number}` : ""),
      c.city,
      c.state,
      c.zip_code,
    ].filter(Boolean);
    return parts.join(", ");
  }, [d.client]);

  if (!d.job) {
    return (
      <div className="min-h-screen bg-[var(--cm-bg)] flex items-center justify-center text-slate-500">
        {t("Carregando…")}
      </div>
    );
  }

  const isAdmin = user?.role === "admin" || user?.role === "supervisor";
  const c = d.client;
  const lat = c?.lat;
  const lng = c?.lng;
  const hasMap = Boolean(lat && lng);
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`;
  const priority = d.job.priority || derivedUrgency(d.job.scheduled_at, d.job.status);

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="mobile-job-detail">
      {/* Mapa Hero */}
      <div className="relative h-72 sm:h-80 bg-slate-200 overflow-hidden">
        {hasMap ? (
          <HeroMap lat={lat} lng={lng} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-slate-200 flex items-center justify-center">
            <MapPin size={48} className="text-blue-400" />
          </div>
        )}

        {/* Back button overlay */}
        <button
          onClick={() => nav(-1)}
          data-testid="mobile-job-back"
          className="absolute top-4 left-4 z-[400] w-11 h-11 rounded-full bg-slate-700/85 hover:bg-slate-800 text-white flex items-center justify-center shadow-lg backdrop-blur"
        >
          <ChevronLeft size={22} />
        </button>

        {/* Navigation overlay */}
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="mobile-job-route"
          className="absolute top-4 right-4 z-[400] w-11 h-11 rounded-full bg-white hover:bg-slate-100 text-slate-800 flex items-center justify-center shadow-lg"
          title={t("Abrir no Google Maps")}
        >
          <Navigation size={20} />
        </a>

        {/* Bottom gradient + property tag */}
        <div className="absolute inset-x-0 bottom-0 pt-16 pb-4 px-4 bg-gradient-to-t from-black/70 via-black/40 to-transparent text-white z-[300]">
          <div className="text-lg font-extrabold leading-tight line-clamp-2 uppercase tracking-tight">
            {c?.name || "—"}
          </div>
          <div className="text-xs text-slate-200 mt-1">
            {c?.address}
            {c?.apt_number ? ` · Apt ${c.apt_number}` : ""}
          </div>
        </div>
      </div>

      <main className="relative -mt-3 bg-white rounded-t-3xl px-5 pt-5 space-y-5 z-[200]">
        {/* Title + priority pill */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1
              className="text-2xl font-bold text-slate-900 leading-tight"
              data-testid="mobile-job-title"
            >
              {c?.name || t("Limpeza")}
            </h1>
            <div className="text-xs text-slate-500 mt-1">
              <StatusBadge status={d.job.status} job={d.job} />
            </div>
          </div>
          <JobPriorityPill value={priority} />
        </div>

        {/* Due on */}
        <div data-testid="mobile-job-due">
          <div className="text-base font-bold text-slate-900 mb-1.5">Due on</div>
          <div className="flex items-center gap-2 text-slate-700">
            <Clock size={16} className="text-slate-400" />
            <span className="text-base">{fmtDueLabel(d.job.scheduled_at, t)}</span>
          </div>
        </div>

        {/* Description / Notes */}
        {(d.job.notes || c?.observations) && (
          <div data-testid="mobile-job-description">
            <div className="text-base font-bold text-slate-900 mb-1.5">{t("Descrição")}</div>
            {c?.observations && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <div className="whitespace-pre-line">{c.observations}</div>
              </div>
            )}
            {d.job.notes && (
              <div className="text-sm text-slate-700 whitespace-pre-line">
                {d.job.notes}
              </div>
            )}
          </div>
        )}

        {/* Door code (destaque) */}
        {c?.door_code && (
          <div
            className="rounded-2xl p-4 bg-blue-600 text-white shadow-md"
            data-testid="mobile-door-code-card"
          >
            <div className="text-[11px] uppercase tracking-widest text-blue-200">
              {t("🔑 Código da porta")}
            </div>
            <div className="text-3xl font-mono font-bold tracking-wider mt-1">
              {c.door_code}
            </div>
          </div>
        )}

        {/* Quick contact — restrito a admin/supervisor (colaborador não vê) */}
        {isAdmin && c?.phone && (
          <div className="cm-card p-3" data-testid="mobile-job-contact">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm flex items-center gap-1">
                <Phone size={14} /> {t("Contato")}
              </h3>
              <a
                href={`tel:+1${c.phone}`}
                className="text-xs font-mono text-blue-700 hover:underline"
              >
                +1 {formatUSPhone(c.phone)}
              </a>
            </div>
            <ContactActions
              client={c}
              size="lg"
              testIdPrefix="mobile-job-contact"
            />
          </div>
        )}

        {/* Checklist */}
        <JobChecklistCard
          checklist={d.checklist}
          jobStatus={d.job.status}
          onToggle={d.toggleChecklistItem}
        />

        {/* Photos */}
        <JobPhotosCard
          before={d.before}
          after={d.after}
          onBeforeChange={d.setBefore}
          onAfterChange={d.setAfter}
        />

        {/* Signature */}
        <SignatureWrap sigRef={d.signatureRef} signature={d.signature} onSaved={d.setSignature} />
      </main>

      {/* Sticky bottom CTA */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        <JobActionPanel
          job={d.job}
          client={c}
          team={d.team}
          busy={d.busy}
          isAdmin={isAdmin}
          onStart={d.startJob}
          onFinish={d.finishJob}
          onSetStatus={d.setStatus}
          onRevertToScheduled={d.revertToScheduled}
        />
      </div>
    </div>
  );
}
